import React, { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '@stax-core/truffle-components';
import { Modal } from '../Modal';
import { IqQuestionsModalProps } from './IqQuestionsModal.types';

const GRAY_900 = '#062333';
const TEXT_SUBTLE = '#6E787A';
const BRAND = '#B93BE4';

const ModalContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: ${GRAY_900};
  margin: 0;
`;

const StepIndicator = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${TEXT_SUBTLE};
  text-transform: uppercase;
`;

const QuestionPrompt = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${GRAY_900};
  margin: 0;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: ${GRAY_900};
  cursor: pointer;

  input[type='radio'] {
    accent-color: ${BRAND};
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }
`;

const ModalFooter = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
  box-shadow: 0px -4px 16px 0px #dddfe480;
`;

const ContinueButton = styled(PrimaryButton)`
  min-width: 121px;
  height: 42px;
  background: ${GRAY_900};
  border: 1px solid ${GRAY_900};
  border-radius: 2px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;

  &:hover,
  &:focus {
    background: ${GRAY_900};
    border-color: ${GRAY_900};
    opacity: 0.9;
  }

  &:disabled {
    background: ${GRAY_900};
    border-color: ${GRAY_900};
    opacity: 1;
  }
`;

const ModalTitle = styled.strong`
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${GRAY_900};
`;

export const IqQuestionsModal: FC<IqQuestionsModalProps> = ({
  isOpen,
  questions,
  onSubmit,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setAnswers({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === questions.length - 1;
  const hasAnswerForCurrentStep = currentQuestion
    ? !!answers[currentQuestion.type]
    : false;

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.type]: e.target.value }));
  };

  const handleContinue = async () => {
    if (!hasAnswerForCurrentStep) return;

    if (isLastStep) {
      setIsSubmitting(true);
      try {
        await onSubmit(answers);
      } catch {
        // Error handling done by parent (hook shows toast)
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  if (!questions.length) return null;

  return (
    <Modal
      isOpen={isOpen}
      isClosable={false}
      onClose={() => undefined}
      width="480px"
      title={<ModalTitle>Verify Identity</ModalTitle>}
      data-testid="iq-questions-modal"
    >
      <ModalContent>
        {currentStep === 0 && (
          <IntroText>
            To confirm it&apos;s really you, we&apos;ll ask a few questions only
            you&apos;d know the answers to.
          </IntroText>
        )}

        <StepIndicator data-testid="step-indicator">
          Question {currentStep + 1} of {questions.length}
        </StepIndicator>

        <QuestionPrompt data-testid="question-prompt">
          {currentQuestion.prompt}
        </QuestionPrompt>

        <RadioGroup>
          {currentQuestion.options.map((option, i) => (
            <RadioLabel key={`${currentStep}-${i}`}>
              <input
                type="radio"
                name={`iq-question-${currentStep}`}
                value={option}
                checked={answers[currentQuestion.type] === option}
                onChange={handleAnswerChange}
                disabled={isSubmitting}
                data-testid={`iq-option-${i}`}
              />
              {option}
            </RadioLabel>
          ))}
        </RadioGroup>
      </ModalContent>

      <ModalFooter>
        <ContinueButton
          data-testid="continue-button"
          disabled={!hasAnswerForCurrentStep || isSubmitting}
          loading={isSubmitting}
          onClick={handleContinue}
          trackingId="iq-continue"
        >
          Continue
        </ContinueButton>
      </ModalFooter>
    </Modal>
  );
};
